import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { endpoint } from '../../utils/endpoint';
import { igLink, ilWspLink } from '../../utils/links';
import abstractLogo from '../../assets/logo-types/abstract.svg';
import combinationLogo from '../../assets/logo-types/combination.svg';
import emblemLogo from '../../assets/logo-types/emblem.svg';
import lettermarkLogo from '../../assets/logo-types/lettermark.svg';
import pictorialLogo from '../../assets/logo-types/pictorial.svg';
import wordmarkLogo from '../../assets/logo-types/wordmark.svg';
import './LogoBriefForm.css';

const LOGO_TYPE_IMAGES = {
    wordmark: wordmarkLogo,
    lettermark: lettermarkLogo,
    pictorial: pictorialLogo,
    abstract: abstractLogo,
    combination: combinationLogo,
    emblem: emblemLogo,
};

const MAX_COLORS = 3;
const LOGO_BRIEF_ENDPOINT = '/.netlify/functions/send-logo-brief';

const fileToAttachment = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const result = reader.result;
            const base64 =
                typeof result === 'string' && result.includes(',')
                    ? result.split(',')[1]
                    : '';
            resolve({
                filename: file.name,
                contentType: file.type || 'application/octet-stream',
                content: base64,
            });
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });

const LogoBriefForm = ({
    copy,
    font = 'ar',
    dir = 'rtl',
    txtAlign = 'right',
    locale = 'ar',
    switchLabel,
    onLocaleSwitch,
}) => {
    const [step, setStep] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState(false);

    const [brandName, setBrandName] = useState('');
    const [tagline, setTagline] = useState('');
    const [industry, setIndustry] = useState('');
    const [logoType, setLogoType] = useState('');
    const [selectedColors, setSelectedColors] = useState([]);
    const [styles, setStyles] = useState([]);
    const [clientName, setClientName] = useState('');
    const [phone, setPhone] = useState('');
    const [email, setEmail] = useState('');
    const [notes, setNotes] = useState('');
    const [referenceFiles, setReferenceFiles] = useState([]);

    const steps = copy.steps;
    const totalSteps = steps.length;

    const logoTypeLabel = useMemo(
        () => copy.logoTypes.find((t) => t.id === logoType)?.title || '',
        [copy.logoTypes, logoType]
    );

    const styleLabels = useMemo(
        () => styles.map((id) => copy.styles.find((s) => s.id === id)?.label || id).join(', '),
        [styles, copy.styles]
    );

    const industryLabel = useMemo(
        () => copy.industries.find((i) => i.id === industry)?.label || '',
        [industry, copy.industries]
    );

    const designerSummary = useMemo(() => {
        const lines = [
            `=== ${copy.designerBriefTitle} ===`,
            `${copy.fields.brandName}: ${brandName}`,
            `${copy.fields.tagline}: ${tagline || '—'}`,
            `${copy.fields.industry}: ${industryLabel || '—'}`,
            `${copy.fields.logoType}: ${logoTypeLabel || '—'}`,
            `${copy.fields.colors}: ${selectedColors.join(', ') || '—'}`,
            `${copy.fields.style}: ${styleLabels || '—'}`,
            `${copy.fields.clientName}: ${clientName}`,
            `${copy.fields.phone}: ${phone}`,
            `${copy.fields.email}: ${email || '—'}`,
            `${copy.fields.notes}: ${notes || '—'}`,
        ];
        return lines.join('\n');
    }, [
        brandName,
        tagline,
        industryLabel,
        logoTypeLabel,
        selectedColors,
        styleLabels,
        clientName,
        phone,
        email,
        notes,
        copy,
    ]);

    const toggleStyle = (id) => {
        setStyles((prev) =>
            prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
        );
    };

    const togglePresetColor = (hex) => {
        setSelectedColors((prev) => {
            if (prev.includes(hex)) return prev.filter((c) => c !== hex);
            if (prev.length >= MAX_COLORS) return prev;
            return [...prev, hex];
        });
    };

    const addCustomColor = (hex) => {
        const normalized = hex.toLowerCase();
        setSelectedColors((prev) => {
            if (prev.includes(normalized)) return prev;
            if (prev.length >= MAX_COLORS) return prev;
            return [...prev, normalized];
        });
    };

    const removeColor = (hex) => {
        setSelectedColors((prev) => prev.filter((c) => c !== hex));
    };

    const canGoNext = () => {
        if (step === 0) return brandName.trim().length >= 2 && industry;
        if (step === 1) return Boolean(logoType);
        if (step === 2) return selectedColors.length > 0 && styles.length > 0;
        if (step === 3) return clientName.trim().length >= 2 && phone.trim().length >= 7;
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!canGoNext()) return;

        setSubmitting(true);
        setError(false);

        const isLocal =
            window.location.hostname === 'localhost' ||
            window.location.hostname === '127.0.0.1';

        try {
            const attachments = await Promise.all(
                referenceFiles.map((file) => fileToAttachment(file))
            );

            const payload = {
                language: locale,
                brandName: brandName.trim(),
                tagline: tagline.trim(),
                industry: industryLabel,
                logoType: logoTypeLabel,
                colors: selectedColors.join(', '),
                colorList: selectedColors,
                styles: styleLabels,
                clientName: clientName.trim(),
                phone: phone.trim(),
                email: email.trim(),
                notes: notes.trim(),
                designerSummary,
                attachments,
            };

            const response = await fetch(LOGO_BRIEF_ENDPOINT, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            if (response.ok) {
                setSubmitted(true);
                setStep(0);
            } else if (isLocal) {
                console.warn(
                    'Logo brief email function unavailable locally. Use `netlify dev` to test emails.'
                );
                setSubmitted(true);
                setStep(0);
            } else {
                throw new Error('submit failed');
            }
        } catch (err) {
            console.error(err);
            if (isLocal) {
                console.warn(
                    'Logo brief email function unavailable locally. Use `netlify dev` to test emails.'
                );
                setSubmitted(true);
                setStep(0);
            } else {
                setError(true);
            }
        } finally {
            setSubmitting(false);
        }
    };

    if (!copy) return null;

    return (
        <div className={`LogoBrief ${font}`} style={{ direction: dir, textAlign: txtAlign }}>
            <div className={`LogoBrief-card ${step === 1 ? 'LogoBrief-card--types' : ''}`}>
                {onLocaleSwitch && switchLabel && (
                    <button
                        type="button"
                        className="LogoBrief-lang-switch"
                        onClick={onLocaleSwitch}
                        aria-label={switchLabel}
                    >
                        {switchLabel}
                    </button>
                )}
                <header className="LogoBrief-header">
                    <img
                        src={`${endpoint}logo-dark.svg`}
                        alt="QB Media"
                        className="LogoBrief-logo"
                    />
                    <h1 className="LogoBrief-title">{copy.title}</h1>
                    <p className="LogoBrief-subtitle">{copy.subtitle}</p>
                </header>

                <div className="LogoBrief-progress" aria-hidden="true">
                    {steps.map((_, i) => (
                        <div
                            key={i}
                            className={`LogoBrief-progress-step ${
                                i <= step ? 'is-active' : ''
                            } ${i < step ? 'is-done' : ''}`}
                        />
                    ))}
                </div>
                <p className="LogoBrief-step-label">
                    {copy.stepOf.replace('{current}', step + 1).replace('{total}', totalSteps)}
                    {' · '}
                    {steps[step]}
                </p>

                {submitted ? (
                    <div className="LogoBrief-success">
                        <div className="LogoBrief-message success">{copy.success}</div>
                        <p className="LogoBrief-success-lead">{copy.successFollowUp}</p>
                        <div className="LogoBrief-success-actions">
                            <a
                                className="LogoBrief-success-btn LogoBrief-success-btn--ig"
                                href={igLink}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {copy.successActions.instagram}
                            </a>
                            <Link className="LogoBrief-success-btn LogoBrief-success-btn--site" to="/">
                                {copy.successActions.website}
                            </Link>
                            <a
                                className="LogoBrief-success-btn LogoBrief-success-btn--wa"
                                href={ilWspLink}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {copy.successActions.whatsapp}
                            </a>
                        </div>
                    </div>
                ) : (
                    <form name="logo-brief" onSubmit={handleSubmit}>
                        <input type="hidden" name="form-name" value="logo-brief" />

                        {step === 0 && (
                            <>
                                <div className="LogoBrief-field">
                                    <label htmlFor="lb-brand">{copy.fields.brandName}</label>
                                    <span className="LogoBrief-hint">{copy.hints.brandName}</span>
                                    <input
                                        id="lb-brand"
                                        className="LogoBrief-input"
                                        value={brandName}
                                        onChange={(e) => setBrandName(e.target.value)}
                                        placeholder={copy.placeholders.brandName}
                                        required
                                    />
                                </div>
                                <div className="LogoBrief-field">
                                    <label htmlFor="lb-tagline">{copy.fields.tagline}</label>
                                    <span className="LogoBrief-hint">{copy.hints.tagline}</span>
                                    <input
                                        id="lb-tagline"
                                        className="LogoBrief-input"
                                        value={tagline}
                                        onChange={(e) => setTagline(e.target.value)}
                                        placeholder={copy.placeholders.tagline}
                                    />
                                </div>
                                <div className="LogoBrief-field">
                                    <span>{copy.fields.industry}</span>
                                    <span className="LogoBrief-hint">{copy.hints.industry}</span>
                                    <div className="LogoBrief-chip-grid" role="radiogroup">
                                        {copy.industries.map((item) => (
                                            <button
                                                key={item.id}
                                                type="button"
                                                className={`LogoBrief-chip ${
                                                    industry === item.id ? 'is-selected' : ''
                                                }`}
                                                onClick={() => setIndustry(item.id)}
                                                aria-pressed={industry === item.id}
                                            >
                                                {item.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </>
                        )}

                        {step === 1 && (
                            <>
                                <p className="LogoBrief-hint LogoBrief-hint--lead">
                                    {copy.hints.logoType}
                                </p>
                                <div className="LogoBrief-type-grid" role="radiogroup">
                                    {copy.logoTypes.map((type) => (
                                        <button
                                            key={type.id}
                                            type="button"
                                            className={`LogoBrief-type-card ${
                                                logoType === type.id ? 'is-selected' : ''
                                            }`}
                                            onClick={() => setLogoType(type.id)}
                                            aria-pressed={logoType === type.id}
                                            aria-label={`${type.title} — ${copy.examplePrefix} ${type.example}`}
                                        >
                                            <div className="LogoBrief-type-visual">
                                                <img
                                                    src={LOGO_TYPE_IMAGES[type.id]}
                                                    alt=""
                                                    loading="lazy"
                                                    draggable="false"
                                                />
                                            </div>
                                            <div className="LogoBrief-type-body">
                                                <span className="LogoBrief-type-name">{type.title}</span>
                                                <span className="LogoBrief-type-example">
                                                    <span className="LogoBrief-type-example-prefix">
                                                        {copy.examplePrefix}
                                                    </span>
                                                    <span className="LogoBrief-type-example-brands">
                                                        {type.example}
                                                    </span>
                                                </span>
                                            </div>
                                            {logoType === type.id && (
                                                <span className="LogoBrief-type-check" aria-hidden="true">✓</span>
                                            )}
                                        </button>
                                    ))}
                                </div>
                                <p className="LogoBrief-type-note">{copy.hints.logoTypeNote}</p>
                            </>
                        )}

                        {step === 2 && (
                            <>
                                <div className="LogoBrief-color-section">
                                    <span>{copy.fields.colors}</span>
                                    <span className="LogoBrief-hint">{copy.hints.colors}</span>
                                    <div className="LogoBrief-color-swatches">
                                        {copy.colorPresets.map((preset) => (
                                            <button
                                                key={preset.hex}
                                                type="button"
                                                className={`LogoBrief-color-swatch ${
                                                    selectedColors.includes(preset.hex)
                                                        ? 'is-selected'
                                                        : ''
                                                }`}
                                                style={{ backgroundColor: preset.hex }}
                                                title={preset.label}
                                                aria-label={preset.label}
                                                onClick={() => togglePresetColor(preset.hex)}
                                            />
                                        ))}
                                    </div>
                                    <div className="LogoBrief-color-custom">
                                        <input
                                            type="color"
                                            defaultValue="#f47a1b"
                                            aria-label={copy.fields.customColor}
                                            onChange={(e) => addCustomColor(e.target.value)}
                                        />
                                        <span className="LogoBrief-hint">{copy.hints.customColor}</span>
                                    </div>
                                    {selectedColors.length > 0 && (
                                        <div className="LogoBrief-selected-colors">
                                            {selectedColors.map((hex) => (
                                                <span key={hex} className="LogoBrief-selected-color">
                                                    <span
                                                        className="LogoBrief-selected-color-dot"
                                                        style={{ backgroundColor: hex }}
                                                    />
                                                    {hex}
                                                    <button
                                                        type="button"
                                                        aria-label={copy.removeColor}
                                                        onClick={() => removeColor(hex)}
                                                    >
                                                        ×
                                                    </button>
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <div className="LogoBrief-field">
                                    <span>{copy.fields.style}</span>
                                    <span className="LogoBrief-hint">{copy.hints.style}</span>
                                    <div className="LogoBrief-chip-grid">
                                        {copy.styles.map((item) => (
                                            <button
                                                key={item.id}
                                                type="button"
                                                className={`LogoBrief-chip ${
                                                    styles.includes(item.id) ? 'is-selected' : ''
                                                }`}
                                                onClick={() => toggleStyle(item.id)}
                                                aria-pressed={styles.includes(item.id)}
                                            >
                                                {item.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </>
                        )}

                        {step === 3 && (
                            <>
                                <div className="LogoBrief-field">
                                    <label htmlFor="lb-name">{copy.fields.clientName}</label>
                                    <input
                                        id="lb-name"
                                        className="LogoBrief-input"
                                        value={clientName}
                                        onChange={(e) => setClientName(e.target.value)}
                                        placeholder={copy.placeholders.clientName}
                                        required
                                    />
                                </div>
                                <div className="LogoBrief-field">
                                    <label htmlFor="lb-phone">{copy.fields.phone}</label>
                                    <input
                                        id="lb-phone"
                                        className="LogoBrief-input"
                                        type="tel"
                                        value={phone}
                                        onChange={(e) => setPhone(e.target.value)}
                                        placeholder={copy.placeholders.phone}
                                        required
                                    />
                                </div>
                                <div className="LogoBrief-field">
                                    <label htmlFor="lb-email">{copy.fields.email}</label>
                                    <input
                                        id="lb-email"
                                        className="LogoBrief-input"
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder={copy.placeholders.email}
                                    />
                                </div>
                                <div className="LogoBrief-field">
                                    <label htmlFor="lb-notes">{copy.fields.notes}</label>
                                    <span className="LogoBrief-hint">{copy.hints.notes}</span>
                                    <input
                                        id="lb-notes"
                                        className="LogoBrief-input"
                                        value={notes}
                                        onChange={(e) => setNotes(e.target.value)}
                                        placeholder={copy.placeholders.notes}
                                    />
                                </div>
                                <div className="LogoBrief-field">
                                    <label htmlFor="lb-files">{copy.fields.references}</label>
                                    <span className="LogoBrief-hint">{copy.hints.references}</span>
                                    <input
                                        id="lb-files"
                                        className="LogoBrief-file"
                                        type="file"
                                        accept="image/*"
                                        multiple
                                        onChange={(e) =>
                                            setReferenceFiles(Array.from(e.target.files || []))
                                        }
                                    />
                                </div>
                                <input type="hidden" name="designerSummary" value={designerSummary} readOnly />
                            </>
                        )}

                        <div className="LogoBrief-actions">
                            {step > 0 && (
                                <button
                                    type="button"
                                    className="LogoBrief-btn LogoBrief-btn--ghost"
                                    onClick={() => setStep((s) => s - 1)}
                                    disabled={submitting}
                                >
                                    {copy.back}
                                </button>
                            )}
                            {step < totalSteps - 1 ? (
                                <button
                                    type="button"
                                    className="LogoBrief-btn LogoBrief-btn--primary"
                                    onClick={() => canGoNext() && setStep((s) => s + 1)}
                                    disabled={!canGoNext()}
                                >
                                    {copy.next}
                                </button>
                            ) : (
                                <button
                                    type="submit"
                                    className="LogoBrief-btn LogoBrief-btn--primary"
                                    disabled={!canGoNext() || submitting}
                                >
                                    {submitting ? copy.submitting : copy.submit}
                                </button>
                            )}
                        </div>

                        {error && (
                            <div className="LogoBrief-message error">{copy.error}</div>
                        )}
                    </form>
                )}
            </div>
        </div>
    );
};

export default LogoBriefForm;
